/**
 * Helper function to get the correct data array based on currentPage
 * @param {*} state
 * @param {*} response
 * @returns Array
 */
export const getFailedProductListData = (state, response) => {
  // If it's the first page, return an empty array to show the "no product found" message.
  if (response.currentPage === 0) return [];

  // For failed cases on subsequent pages, there is no new data in the response.
  // Return the existing data from the state to avoid affecting already loaded pages.
  return state?.details?.data || [];
};

/**check display tag sequence */
export const returnDisplayTag = (cmsAttribute, productAttributes, position) => {
  return cmsAttribute[productAttributes?.find((element) => cmsAttribute[element]?.position === position)];
};

/**
 * Initializes and manages the sequential playback of product videos
 * on the mobile PLP (Product Listing Page) based on scroll visibility.
 *
 * - Uses IntersectionObserver to track which videos are in view.
 * - Plays videos one by one in a queue.
 * - Resets videos when they leave the viewport.
 * - Delays playback between videos for smoother transitions.
 */
export const playMobilePlpVideo = () => {
  const options = {
    root: null,
    rootMargin: "0px",
    threshold: [0, 0.7],
  };
  const classes = ["absolute", "top-0", "left-0"];
  const inViewVideos = [];
  let current = 0;
  const delayTime = 1000;
  const scrollDelayTime = 500;
  let videoTimeout = null;

  const videos = Array.from(document.querySelectorAll("#plpProduct video"));

  /**
   * Plays videos in the `inViewVideos` queue sequentially.
   * - Skips videos that have already been played.
   * - Ensures video is loaded before playback.
   * - Hides placeholder image while playing.
   * - Waits `delayTime` before moving to the next video.
   *
   */
  const playVideoQueue = () => {
    if (videoTimeout) clearTimeout(videoTimeout);
    if (current >= inViewVideos.length) return;
    const videoObj = inViewVideos[current];
    if (!videoObj || videoObj.isPlayed) {
      current++;
      playVideoQueue();
      return;
    }

    const { video } = videoObj;

    // Check if already playing
    const isPlaying = video.currentTime > 0 && !video.paused && !video.ended && video.readyState > 2;
    if (isPlaying) {
      return;
    }

    if (!video.src) {
      video.src = video.getAttribute("data-src");
      video.load();
    }
    if (!video.nextElementSibling.classList.contains("hidden")) {
      video.nextElementSibling.classList.remove(...classes);
      video.nextElementSibling.classList.add("hidden");
    }
    video.play().catch((error) => {
      console.log(error);
      current++;
      playVideoQueue();
      return;
    });
    video.onended = () => {
      videoObj.isPlayed = true;
      video.pause();
      video.currentTime = 0;
      current++;
      video.nextElementSibling.classList.remove("hidden");
      video.nextElementSibling.classList.add(...classes);
      // Wait for delayTime before starting
      videoTimeout = setTimeout(() => {
        playVideoQueue();
      }, delayTime);
    };
  };

  /**
   * IntersectionObserver callback that tracks which videos are in view.
   * - Adds visible videos to `inViewVideos` if not already present.
   * - Removes videos when they go out of view.
   * - Resets and unloads videos that leave the viewport (if not fully loaded).
   *
   */
  const onInterProductTiles = (entries) => {
    entries.forEach((entry) => {
      const video = entry.target;

      if (entry.isIntersecting && entry.intersectionRatio >= 0.7) {
        // Add video if not already in queue
        if (!inViewVideos.some((obj) => obj.video === video)) {
          inViewVideos.push({ video, isPlayed: false });
        }
      } else {
        // Remove video from queue
        const indexOf = inViewVideos.findIndex((obj) => obj.video === video);
        if (indexOf !== -1) {
          inViewVideos.splice(indexOf, 1);
        }

        // Pause and reset
        video.pause();
        video.currentTime = 0;
        // Only remove src if video is NOT fully loaded
        const fullyLoaded =
          video.readyState === 4 && video.buffered.length > 0 && video.buffered.end(0) >= video.duration;

        if (!fullyLoaded) {
          video.removeAttribute("src");
          video.load();
        }
      }
    });

    current = 0;
    setTimeout(() => {
      if (inViewVideos.length > 0) playVideoQueue();
    }, scrollDelayTime);
  };

  // Start observing videos
  const observer = new IntersectionObserver(onInterProductTiles, options);
  videos.forEach((video) => observer.observe(video));
};
